/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { Checkbox } from '@fluentui/react/lib/Checkbox';
import { ILinkProps, Link } from '@fluentui/react/lib/Link';
import { Stack } from '@fluentui/react/lib/Stack';
import { Property } from 'csstype';
import * as React from 'react';
import { defineMessages } from 'react-intl';
import { LuisText, LuisTextProps } from 'src/core/components/LuisText';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { getScopedTheme, getStylist } from 'src/core/themes/luisStylist';
import { FilterItemWithCheckbox } from 'src/verticals/utterancesFilters/components/FilterItemWithCheckbox';
import { FiltersItem, SelectionChange } from 'src/verticals/utterancesFilters/types/Filters';

export type FiltersSectionProps = {
    /**
     * The title of the block
     */
    title: string;

    /**
     * Items to be rendered in the block
     */
    items: FiltersItem[];

    /**
     * Max number of items shown in the block
     */
    itemsLimit?: number;

    /**
     * Handler function for the item selection
     */
    onSelectionChange: (change: SelectionChange) => void;

    /**
     * Handler function for see all button
     */
    onSeeAllBtnClick?: () => void;

    /**
     * A flag to show option for selecting all or not.
     */
    showSelectAll?: boolean;

    /**
     * A Callback fires when select all options is toggled.
     */
    onToggleSelectAll?: (isChecked: boolean) => void;
};

const messages = defineMessages({
    seeAll: {
        id: 'core.filtersSection.seeAll',
        defaultMessage: 'See all'
    },
    seeAllAriaLabel: {
        id: 'core.filtersSection.seeAllAriaLabel',
        defaultMessage: 'See all {itemsName}'
    },
    all: {
        id: 'core.filtersSection.all',
        defaultMessage: 'All'
    },
    emptyStateMsg: {
        id: 'core.filtersSection.emptyStateMsg',
        defaultMessage: 'No items available'
    }
});

const scopedTheme = getScopedTheme('FiltersSection');
const { styleComponent } = getStylist('FiltersSection');

const Title = styleComponent<LuisTextProps>(LuisText)('Title', theme => ({ color: theme.titlesColor, fontSize: 14 }));
const EmptyStateMsg = styleComponent<LuisTextProps>(LuisText)('EmptyStateMsg', { fontStyle: 'italic' });
const EntitiesSection = styleComponent<LuisTextProps>(Stack)('EntitiesSection', { marginLeft: 8 });
const SeeAll = styleComponent<ILinkProps>(Link)('SeeAll', theme => ({
    color: theme.primaryColor,
    fontSize: 12,
    marginTop: 8,
    overflow: 'hidden',
    $nest: { '&:hover, &:active': { color: theme.primaryColor } }
}));

export const FiltersSection = (props: FiltersSectionProps) => {
    const { title, items, onSelectionChange, onSeeAllBtnClick = null, itemsLimit = 5, showSelectAll = false, onToggleSelectAll } = props;
    const { localize } = useLocalization();

    return (
        <Stack tokens={{ childrenGap: 12 }}>
            <Title fontWeight={scopedTheme.titlesFontWeight as Property.FontWeight}>{title}</Title>
            {items.length > 0 ? (
                <EntitiesSection tokens={{ childrenGap: 16 }}>
                    {showSelectAll && (
                        <Checkbox
                            label={localize(messages.all)}
                            checked={items.reduce((acc, item) => acc && item.isChecked, true)}
                            onChange={(_, isChecked) => onToggleSelectAll(isChecked)}
                        />
                    )}

                    {items.slice(0, itemsLimit).map(item => (
                        <FilterItemWithCheckbox key={item.id} item={item} onSelectionChange={onSelectionChange} />
                    ))}
                </EntitiesSection>
            ) : (
                <EmptyStateMsg>{localize(messages.emptyStateMsg)}</EmptyStateMsg>
            )}
            {items.length > itemsLimit ? (
                <SeeAll onClick={onSeeAllBtnClick} as="button" aria-label={localize(messages.seeAllAriaLabel, { itemsName: title })}>
                    {localize(messages.seeAll)}
                </SeeAll>
            ) : null}
        </Stack>
    );
};
